import {
  AsyncButton,
  DialogDescription,
  DialogTitle,
  showConfirmationPrompt,
  showDialog,
  Skeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useIsMobile,
} from '@bpinternal/ui-kit-next'
import { CheckIcon } from 'lucide-react'
import { useState } from 'react'
import { Boundary, PlanBadge } from '~/components'
import { Badge } from '~/elements'
import { useSuspenseQuery } from '~/services'
import { cn, formatters } from '~/utils'
import { COMMUNITY_PLAN_PRODUCT_ID } from '../constants'
import { useCustomerPortal } from '../hooks'
import { usePlanProducts } from '../hooks/usePlanProducts'
import { usePurchasePlan } from '../hooks/usePurchasePlan'
import { useSaleEvent } from '../hooks/useSaleEvent'
import { Plan, PlanInfo, PlanInfoMap } from '../types/index'
import { getOrderedPlansInfo, isUpgrade } from '../utils/plan'
import { showDowngradePlanDialog } from './DowngradePlanDialog'

export const showPlanSelectionDialog = ({ workspaceId }: { workspaceId: string }) => {
  return showDialog(({ close }) => <PlanSelectionDialog workspaceId={workspaceId} close={close} />, {
    size: 'max-w-7xl',
  })
}

const showYearlyDowngradeDialog = (openCustomerPortal: () => void) => {
  showConfirmationPrompt(
    <p>You must first cancel your current plan and resubscribe at the end of your billing cycle.</p>,
    {
      confirmLabel: 'OK',
      title: 'Subscription management',
    }
  ).then((result) => {
    if (result) {
      openCustomerPortal()
    }
  })
}

const colorClassNameMap: Record<PlanInfo['legacyName'], string> = {
  community: 'bg-gray-400',
  plus: 'bg-blue-600',
  team: 'bg-green-600',
  enterprise: 'bg-black',
}

type PlanSelectionProps = { workspaceId: string; close: () => void }

export const PlanSelectionDialog = ({ workspaceId, close }: PlanSelectionProps) => {
  const [showYearly, setShowYearly] = useState(false)

  return (
    <div className="flex h-[75vh] flex-col items-center gap-6 overflow-y-auto lg:h-full lg:max-h-[90vh] lg:gap-5">
      <div className="flex w-full flex-col justify-between gap-6 text-center lg:flex-row lg:px-6">
        <div className="hidden justify-self-center lg:flex">
          <Tabs
            value={showYearly ? 'annual' : 'monthly'}
            onValueChange={(value) => {
              setShowYearly(value === 'annual')
            }}
          >
            <TabsList>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="annual">Annual</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <DialogTitle className="text-xl font-semibold lg:text-3xl">Choose Your Plan</DialogTitle>
        <DialogDescription className="text-foreground lg:text-end">
          Need help choosing a plan?{' '}
          <a
            href="https://www.botpress.com/en/contact-us"
            target="_blank"
            rel="noreferrer"
            className="font-medium underline"
          >
            Talk to Sales
          </a>
        </DialogDescription>
      </div>
      <Boundary
        suspenseFallback={
          <div className="flex w-full flex-col items-center gap-6 lg:flex-row">
            <Skeleton className="h-9 w-full lg:h-[500px]" />
            <Skeleton className="h-6 w-1/2 lg:h-[500px] lg:w-full" />
            <Skeleton className="h-[500px] w-full lg:block" />
            <Skeleton className="hidden h-[500px] w-full lg:block" />
          </div>
        }
      >
        <PlanSelectionDialogContent
          workspaceId={workspaceId}
          close={close}
          showYearly={showYearly}
          setShowYearly={setShowYearly}
        />
      </Boundary>
      <div className="hidden flex-col items-center gap-1 text-sm text-foreground lg:flex">
        <span>Prices in USD</span>
        <span>
          Need more information?{' '}
          <a href="https://botpress.com/pricing" target="_blank" rel="noreferrer" className="underline">
            View full details on our pricing page
          </a>
        </span>
      </div>
    </div>
  )
}

const PlanSelectionDialogContent = ({
  workspaceId,
  close,
  showYearly,
  setShowYearly,
}: PlanSelectionProps & { showYearly: boolean; setShowYearly: (showYearly: boolean) => void }) => {
  const {
    data: { billingVersion },
  } = useSuspenseQuery('workspaces_/$workspaceId_', { workspaceId })
  const orderedPlans = getOrderedPlansInfo()
  const availablePlans =
    billingVersion !== 'v3'
      ? Object.fromEntries(Object.entries(orderedPlans).filter(([planLegacyName, _]) => planLegacyName !== 'plus'))
      : orderedPlans

  return (
    <>
      {/* Mobile view */}
      <Tabs defaultValue={COMMUNITY_PLAN_PRODUCT_ID} className="flex w-full flex-col items-center gap-6 lg:hidden">
        <TabsList>
          {Object.values(availablePlans).map((plan) => (
            <TabsTrigger key={plan.productId} value={plan.productId}>
              {plan.displayName === 'Pay-as-you-go' ? 'Free' : plan.displayName}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="flex flex-row items-center gap-2">
          <Switch checked={showYearly} onCheckedChange={setShowYearly} />
          Annual
        </div>
        {Object.values(availablePlans).map((plan) => (
          <TabsContent key={plan.productId} value={plan.productId} className="mt-0 w-full">
            <PlanCard plan={plan} close={close} workspaceId={workspaceId} showYearlyPrice={showYearly} />
          </TabsContent>
        ))}
      </Tabs>

      {/* Desktop view */}
      <div className={cn('hidden grid-cols-4 gap-x-6 lg:grid xl:px-6', billingVersion !== 'v3' && 'grid-cols-3')}>
        {Object.values(availablePlans).map((plan) => (
          <PlanCard
            key={plan.productId}
            workspaceId={workspaceId}
            plan={plan}
            showYearlyPrice={showYearly}
            close={close}
          />
        ))}
      </div>
    </>
  )
}

type PlanCardProps = {
  workspaceId: string
  close: () => void
  plan: PlanInfoMap[Plan]
  showYearlyPrice: boolean
}

const PlanCard = ({ workspaceId, close, plan, showYearlyPrice }: PlanCardProps) => {
  const { planProducts, currentPlan } = usePlanProducts(workspaceId)
  const { mutate: purchasePlan, isPending } = usePurchasePlan(workspaceId, close)
  const { mutate: openCustomerPortal } = useCustomerPortal(workspaceId)
  const saleEvent = useSaleEvent(workspaceId)
  const isMobile = useIsMobile()

  // Hack to display the undiscounted price during a sale event
  const monthlyPlanProduct = planProducts
    .filter((product) => product.cycle === 'monthly')
    .find((product) => product.id === plan.productId) ?? {
    id: 'managed-product-id',
    legacyName: 'enterprise',
    cycle: 'monthly',
    priceId: 'managed-price-id',
    pricePerMonth: 149500,
  }

  const yearlyPlanProduct = planProducts
    .filter((product) => product.cycle === 'yearly')
    .find((product) => product.id === plan.productId) ?? {
    id: 'managed-product-id',
    legacyName: 'enterprise',
    cycle: 'yearly',
    priceId: 'managed-price-id',
    pricePerMonth: 99500,
  }

  const planProduct = showYearlyPrice ? yearlyPlanProduct : monthlyPlanProduct

  const isPlanUpgrade = isUpgrade(currentPlan.legacyName, plan.legacyName)

  const isCurrentPlan = showYearlyPrice
    ? currentPlan.cycle === 'yearly' && currentPlan.legacyName === plan.legacyName
    : currentPlan.cycle === 'monthly' && currentPlan.legacyName === plan.legacyName
  const samePlanButDifferentCycle = !isCurrentPlan && currentPlan.legacyName === plan.legacyName

  const disabled =
    (currentPlan.legacyName === 'community' && samePlanButDifferentCycle) ||
    isCurrentPlan ||
    isPending ||
    currentPlan.legacyName === 'enterprise'

  let selectPlanButtonProps: { text: string; onClick: () => void }
  if (isCurrentPlan) {
    selectPlanButtonProps = {
      text: 'Current plan',
      onClick: () => {},
    }
  } else if (samePlanButDifferentCycle) {
    if (plan.legacyName === 'community') {
      selectPlanButtonProps = {
        text: 'Current plan',
        onClick: () => {},
      }
    } else if (currentPlan.cycle === 'yearly') {
      selectPlanButtonProps = {
        text: 'Switch to Monthly',
        onClick: () => showYearlyDowngradeDialog(openCustomerPortal),
      }
    } else {
      selectPlanButtonProps = {
        text: 'Switch to Annual',
        onClick: () => purchasePlan({ desiredPlan: planProduct, isYearly: showYearlyPrice }),
      }
    }
  } else if (plan.legacyName === 'enterprise') {
    selectPlanButtonProps = {
      text: 'Contact Us',
      onClick: () => window.open('https://botpress.com/contact-us', '_blank'),
    }
  } else if (isPlanUpgrade) {
    if (currentPlan.cycle === 'yearly' && !currentPlan.cancelAtPeriodEnd) {
      selectPlanButtonProps = {
        text: `Upgrade to ${plan.displayName}`,
        onClick: () => showYearlyDowngradeDialog(openCustomerPortal),
      }
    } else {
      selectPlanButtonProps = {
        text: `Upgrade to ${plan.displayName}`,
        onClick: () => purchasePlan({ desiredPlan: planProduct, isYearly: showYearlyPrice }),
      }
    }
  } else {
    if (currentPlan.cycle === 'yearly') {
      selectPlanButtonProps = {
        text: `Downgrade to ${plan.displayName}`,
        onClick: () => showYearlyDowngradeDialog(openCustomerPortal),
      }
    } else {
      selectPlanButtonProps = {
        text: `Downgrade to ${plan.displayName}`,
        onClick: () =>
          showDowngradePlanDialog({
            desiredPlanId: planProduct.id,
            isYearly: showYearlyPrice,
            workspaceId,
          }),
      }
    }
  }

  const planPrice =
    saleEvent && planProduct.legacyName !== 'enterprise' ? monthlyPlanProduct.pricePerMonth : planProduct.pricePerMonth

  const showBilledBadge = plan.legacyName === 'plus' || plan.legacyName === 'team' || plan.legacyName === 'enterprise'
  const mostPopular = plan.legacyName === 'team'

  let backgroundSize
  if (plan.legacyName === 'enterprise') {
    backgroundSize = 'cover'
  } else if (isMobile) {
    backgroundSize = '150%'
  } else {
    backgroundSize = '220%'
  }

  const planDiscount = saleEvent?.planDiscounts[plan.legacyName]

  return (
    <div
      className={cn(
        'flex flex-1 flex-col overflow-clip rounded-xl lg:mt-7',
        mostPopular && 'lg:mt-0 lg:border-2 lg:border-foreground lg:bg-foreground'
      )}
    >
      {mostPopular && (
        <div className="hidden h-6 w-full items-center justify-center text-xs font-semibold text-background lg:flex">
          Most Popular
        </div>
      )}
      <div
        className={cn(
          'relative flex h-[188px] w-full flex-col justify-between gap-2 overflow-clip rounded-t-xl p-5 text-white lg:h-[160px]',
          colorClassNameMap[plan.legacyName]
        )}
        style={
          plan.backgroundUrl
            ? {
                backgroundImage: `url(${plan.backgroundUrl})`,
                backgroundSize,
              }
            : undefined
        }
      >
        <div className="space-y-2">
          <div className="flex flex-row items-center gap-2">
            <span className="text-lg font-medium">{plan.displayName}</span>
            <div className="flex min-w-0 gap-1">
              {saleEvent && planDiscount && (
                <Badge className="whitespace-nowrap bg-white text-black">{`${formatters.percent(planDiscount.percentOff)} OFF${showYearlyPrice ? '' : ' 3 MONTHS'}`}</Badge>
              )}
              {showYearlyPrice && showBilledBadge && (
                <Badge className="min-w-0 bg-white bg-opacity-50 text-white">
                  <span className="truncate">Billed Annually</span>
                </Badge>
              )}
              {mostPopular && <Badge className="bg-white text-black lg:hidden">Most Popular</Badge>}
            </div>
          </div>
          <p className="text-xs">{plan.description}</p>
        </div>
        <div className="flex flex-row items-end gap-1">
          <span className="text-3xl font-medium">
            {saleEvent && planDiscount
              ? formatters.$centdollars(planPrice - planPrice * planDiscount.percentOff, 0)
              : formatters.$centdollars(planPrice, 0)}
          </span>
          {saleEvent && planDiscount && (
            <span className="text-xl line-through opacity-40">
              {formatters.$centdollars(
                planProduct.legacyName === 'enterprise' ? planProduct.pricePerMonth : monthlyPlanProduct.pricePerMonth,
                0
              )}
            </span>
          )}
          <div className="flex flex-col whitespace-nowrap pb-0.5 text-sm font-normal tracking-tight">
            <span>{plan.priceSuffix}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-4 bg-neutral-50 px-5 pb-5 pt-4">
        <AsyncButton className="h-12" onClick={selectPlanButtonProps.onClick} disabled={disabled} loading={isPending}>
          <span className="truncate">{selectPlanButtonProps.text}</span>
        </AsyncButton>

        <div className="flex flex-col gap-3 text-xs font-medium lg:h-[132px]">
          {plan.previousPlan && (
            <div className="flex flex-row items-center gap-2 text-foreground">
              <CheckIcon size={16} />
              <span className="truncate">Everything in </span>
              <PlanBadge plan={plan.previousPlan} className="text-xs" />
            </div>
          )}
          {plan.features.map((feature, index) => (
            <div key={index} className="flex flex-row items-center gap-2 text-foreground">
              <CheckIcon size={16} />
              {feature}
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-4 text-xs font-medium">
          {plan.addons && (
            <>
              <span>Add-ons</span>
              {plan.addons.map((addon, index) => (
                <div className="text-foreground" key={index}>
                  {addon.display}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
